/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * The advertising identifier sent when none is available.
 *
 * Mandated by the TiVo Plus Client Ad Parameter Specification. The parameter
 * must never be blank: a blank value plays perfectly well while silently
 * destroying ad targeting, so the all-zero UUID makes the absence explicit
 * to the ad server.
 */
export const ZERO_UUID: string = "00000000-0000-0000-0000-000000000000";

/** Placeholder standing in for the `//` of the URL scheme. */
export const TIVOPLUS_BLANK: string = "TIVOPLUS_BLANK";

/**
 * Query parameter identifying a XUMO-delivered stream.
 *
 * XUMO also acts as an online video provider for third-party content, so
 * XUMO's encoding rules are selected by the presence of this parameter in
 * the URL rather than by the content partner's identity — which is what
 * makes third-party channels delivered through XUMO pick up the same rules,
 * and no other provider's channels pick them up by mistake.
 */
export const XUMO_MARKER_PARAM: string = "ads.xumo_channelId";

/** The grouped key-value block whose payload XUMO reads separately. */
export const XUMO_KVPS_PARAM: string = "ads.tivo_kvps=";

/**
 * Everything a resolver needs to fill in a TiVo+ template.
 *
 * Split by how the value is treated, not by where it comes from:
 * `literal` values are safe constants or already-sanitised device facts and
 * are substituted verbatim, while `identity` values are viewer- or
 * device-bearing free text and are always encoded.
 */
export type TTivoPlusValues = {
  // ── Literal values — substituted verbatim ──────────────────────────────
  appName: string;
  platform: string;
  /** Content type, e.g. `Linear`. */
  contentType: string;
  /** Device brand, e.g. `Samsung`/`LG`/`Amazon`/`Apple`/`Microsoft`. */
  deviceBrand: string;
  openRtbDeviceType: string;
  /** Advertising device-identifier type, e.g. `tifa`/`lgudid`/`afai`/`dpid`. */
  adDeviceType: string;
  /** Ad unit classification, e.g. `video` or `test_ad_unit`. */
  adUnitType: string;
  /** Limit-ad-tracking flag, `"1"` or `"0"`. */
  limitAdTracking: string;
  usPrivacy: string;
  gdpr: string;
  gdprConsent: string;
  /**
   * IAB TCF consent string, mirrored from `gdprConsent` when both `gdpr` and
   * `gdprConsent` carry a value, empty otherwise.
   */
  tcString: string;
  /** IAB GPP string. Sent empty — not yet supported by this client. */
  gppString: string;
  /** IAB GPP applicable-section IDs. Sent empty — not yet supported. */
  gppSidString: string;
  /** Device language, ISO-639-1. */
  deviceLanguage: string;
  deviceCountry: string;
  /** Application version, restricted to URL-safe characters. */
  appVersion: string;
  /** Screen size as `WIDTHxHEIGHT`. */
  adSize: string;

  // ── Identity-bearing values — always encoded ───────────────────────────
  advertisingId: string;
  /** User-session identifier, stable for at least 24 hours. */
  userSessionId: string;
  mvpd: string;
  deviceMake: string;
  deviceModel: string;
};

/** Per-playback values that vary by channel rather than by device. */
export type TTivoPlusContext = {
  channelName: string;
  /** Genre/category label(s) for the current program, comma-separated. */
  contentGenre: string;
  /**
   * Resolved parental-rating display name for the current program. A single
   * value — when the program carries multiple ratings, only the first
   * resolved one is used here.
   */
  contentRating: string;
};

/** How a placeholder's value must be treated before substitution. */
export enum PlaceholderKind {
  /**
   * Substituted verbatim. These are constants or already-constrained device
   * facts that contain no character needing escaping.
   */
  Literal = "literal",

  /**
   * Encoded before substitution, and stripped of characters that break
   * XUMO's parser when the value sits outside the grouped block.
   */
  Identity = "identity"
}

/** A placeholder and how to resolve it. */
export type TPlaceholderSpec = {
  /** The token as it appears in a template, without delimiters. */
  token: string;
  /** How the resolved value must be treated. */
  kind: PlaceholderKind;
  /** Reads the value from the resolved inputs. */
  read: (values: TTivoPlusValues, context: TTivoPlusContext) => string;
};

/**
 * Every placeholder this client resolves.
 *
 * Order is irrelevant — see `PLACEHOLDER_PATTERN` in `TivoPlusUrlResolver`
 * for why a token that is a strict prefix of another is still safe. One such
 * pair exists today, `TIVOPLUS_GDPR` inside `TIVOPLUS_GDPRCONSENT`, and is
 * covered by tests.
 */
export const TIVO_PLUS_PLACEHOLDERS: ReadonlyArray<TPlaceholderSpec> = [
  // Structural. Normally resolved before URL extraction, but kept here so
  // resolution is idempotent whichever order a caller uses.
  { token: TIVOPLUS_BLANK, kind: PlaceholderKind.Literal, read: () => "" },

  // Literals.
  {
    token: "TIVOPLUS_APPNAME",
    kind: PlaceholderKind.Literal,
    read: (v) => v.appName
  },
  {
    token: "TIVOPLUS_PLATFORM",
    kind: PlaceholderKind.Literal,
    read: (v) => v.platform
  },
  {
    token: "TIVOPLUS_CONTENT_TYPE",
    kind: PlaceholderKind.Literal,
    read: (v) => v.contentType
  },
  {
    token: "TIVOPLUS_OPENRTB_DEVTYPE",
    kind: PlaceholderKind.Literal,
    read: (v) => v.openRtbDeviceType
  },
  {
    token: "TIVOPLUS_DEV_TYPE",
    kind: PlaceholderKind.Literal,
    read: (v) => v.adDeviceType
  },
  {
    token: "TIVOPLUS_ADUNIT_TYPE",
    kind: PlaceholderKind.Literal,
    read: (v) => v.adUnitType
  },
  {
    token: "TIVOPLUS_DEV_LAT",
    kind: PlaceholderKind.Literal,
    read: (v) => v.limitAdTracking
  },
  {
    token: "TIVOPLUS_USPRIVACY",
    kind: PlaceholderKind.Literal,
    read: (v) => v.usPrivacy
  },
  {
    token: "TIVOPLUS_GDPRCONSENT",
    kind: PlaceholderKind.Literal,
    read: (v) => v.gdprConsent
  },
  {
    token: "TIVOPLUS_GDPR",
    kind: PlaceholderKind.Literal,
    read: (v) => v.gdpr
  },
  {
    token: "TIVOPLUS_TC_STRING",
    kind: PlaceholderKind.Literal,
    read: (v) => v.tcString
  },
  {
    token: "TIVOPLUS_GPP_STRING",
    kind: PlaceholderKind.Literal,
    read: (v) => v.gppString
  },
  {
    token: "TIVOPLUS_GPPSID_STRING",
    kind: PlaceholderKind.Literal,
    read: (v) => v.gppSidString
  },
  {
    token: "TIVOPLUS_DEVLANG",
    kind: PlaceholderKind.Literal,
    read: (v) => v.deviceLanguage
  },
  {
    token: "TIVOPLUS_DEVCOUNTRY",
    kind: PlaceholderKind.Literal,
    read: (v) => v.deviceCountry
  },
  {
    token: "TIVOPLUS_APP_VERSION",
    kind: PlaceholderKind.Literal,
    read: (v) => v.appVersion
  },
  {
    token: "TIVOPLUS_AD_SIZE",
    kind: PlaceholderKind.Literal,
    read: (v) => v.adSize
  },

  // Literals the specification requires to be sent empty for this client.
  {
    token: "TIVOPLUS_CONTENTLANG",
    kind: PlaceholderKind.Literal,
    read: () => ""
  },
  {
    token: "TIVOPLUS_DEVBRAND",
    kind: PlaceholderKind.Literal,
    read: (v) => v.deviceBrand
  },
  {
    token: "TIVOPLUS_MANUFACTURING_DATE",
    kind: PlaceholderKind.Literal,
    read: () => ""
  },

  // Identity-bearing.
  {
    token: "TIVOPLUS_DEV_ID",
    kind: PlaceholderKind.Identity,
    read: (v) => v.advertisingId
  },
  {
    token: "TIVOPLUS_USERSESSION_ID",
    kind: PlaceholderKind.Identity,
    read: (v) => v.userSessionId
  },
  {
    token: "TIVOPLUS_CHANNELNAME",
    kind: PlaceholderKind.Identity,
    read: (_v, c) => c.channelName
  },
  {
    token: "TIVOPLUS_CONTENTGENRE",
    kind: PlaceholderKind.Identity,
    read: (_v, c) => c.contentGenre
  },
  {
    token: "TIVOPLUS_CONTENTRATING",
    kind: PlaceholderKind.Identity,
    read: (_v, c) => c.contentRating
  },
  {
    token: "TIVOPLUS_MVPD",
    kind: PlaceholderKind.Identity,
    read: (v) => v.mvpd
  },
  {
    token: "TIVOPLUS_DEVMAKE",
    kind: PlaceholderKind.Identity,
    read: (v) => v.deviceMake
  },
  {
    token: "TIVOPLUS_DEVMODEL",
    kind: PlaceholderKind.Identity,
    read: (v) => v.deviceModel
  }
];
